import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { trackLinkClick } from '@atlassian/temp-analytics';
import OfficeBuildingIcon from '@atlaskit/icon/glyph/office-building';
import EntitlementIcon from '@css-xp/base/entitlement-icon';
import { EntitlementDetails, Maybe, ProvisionedResourceType } from '@css-xp/graphtypes/src/generated-types-new';
import { NoData } from '@css-xp/ppc-common';
import { MILLISECONDS_IN_DAY } from '../../../../constants/widget';
import './widget-items.scss';

export const renderSubscriptionWidgetTimestamp = (
  timestamp: Maybe<string | number> | undefined,
  belowDayTimestampContent?: React.ReactNode,
  timePrefix?: React.ReactNode,
  shouldHidePrefix?: boolean
) => {
  if (!timestamp) {
    return <NoData />;
  }
  return (
    <div className="timestamp">
      {belowDayTimestampContent && new Date(timestamp).getTime() - new Date().getTime() <= MILLISECONDS_IN_DAY ? (
        belowDayTimestampContent
      ) : (
        <>
          {timePrefix} {formatDistanceToNowStrict(new Date(timestamp), { addSuffix: !shouldHidePrefix })}
        </>
      )}
    </div>
  );
};

type SubscriptionWidgetItemType = Pick<
  EntitlementDetails,
  'productName' | 'productId' | 'cloudSiteHostname' | 'logoId'
> & {
  url: string;
  timestamp: Maybe<string | number> | undefined;
  timePrefix?: React.ReactNode;
  belowDayTimestampContent?: React.ReactNode;
  heading?: string;
  orgName?: Maybe<string>;
  orgProvisionedResourceType?: Maybe<string>;
  version?: number;
};
/**
 * Component for Subscription Widget on the Home page
 * @constructor
 */
const SubscriptionWidgetItemsComponent = ({
  url,
  timestamp,
  productId,
  productName,
  cloudSiteHostname,
  orgName,
  orgProvisionedResourceType,
  timePrefix = '',
  logoId,
  belowDayTimestampContent,
  heading = '',
  version,
}: SubscriptionWidgetItemType) => {
  const shouldShowOrgInfo = useMemo(() => orgProvisionedResourceType === ProvisionedResourceType.ORGANISATION, [
    orgProvisionedResourceType,
  ]);
  const isVersion2 = version === 2;
  const renderSubTitle = () => {
    return shouldShowOrgInfo ? (
      <div className="sub-title organization-info">
        <div className="organization-icon">
          <OfficeBuildingIcon label="" size="small" />
        </div>
        <div className="line-ellipsis">{orgName}</div>
      </div>
    ) : (
      <div className="sub-title line-ellipsis">{cloudSiteHostname}</div>
    );
  };
  const renderTitle = () => {
    return <div className="title line-ellipsis">{productName}</div>;
  };

  return (
    <div className="flex justify-space-between mb-12 pv-12 subscription-widget-item">
      <div className="left-side">
        <div className="content">
          <Link
            to={url}
            onClick={() => {
              trackLinkClick('widgetItemClick', { source: heading });
            }}
          >
            <div className="flex">
              <EntitlementIcon
                productId={productId ?? ''}
                isCloud={true}
                className="avatar medium entitlement-icon"
                logoId={logoId ?? ''}
              />
              {isVersion2 ? (
                <div className="align-self-center" data-testid="title-container">
                  {renderTitle()}
                  {renderSubTitle()}
                </div>
              ) : (
                renderTitle()
              )}
            </div>
          </Link>
        </div>
        {isVersion2 || <div className="content">{renderSubTitle()}</div>}
      </div>
      <div className="right-side">
        <div className="content">
          {renderSubscriptionWidgetTimestamp(timestamp, belowDayTimestampContent, timePrefix)}
        </div>
      </div>
    </div>
  );
};

export const SubscriptionWidgetItem = React.memo(SubscriptionWidgetItemsComponent);
